<template>
  <div class="body-wrapper section content has-text-justified">
    <section>
      <h1 class="title is-2">Apple Pay&#8482; Web Integration</h1>

      <div class="mb-4">
        By using Apple Pay, you agree to all Apple Pay terms and conditions.
        See the following links for more information:
      </div>
      <div>
        <a target="_blank" href="https://developer.apple.com/apple-pay/acceptable-use-guidelines-for-websites/">Acceptable
          Use Policy</a>
      </div>
      <div>
        <a target="_blank" href="https://developer.apple.com/support/terms/apple-developer-program-license-agreement/">
          Apple Developer Program License Agreement
        </a>
      </div>
    </section>

    <section>
      <h3>Integration steps:</h3>

      <ol class="bottom-margin">
        <div class="mb-4">
          For an overview of the integration process, and an explanation of the terms used, see
          <a target="_blank" href="https://developer.apple.com/documentation/apple_pay_on_the_web/configuring_your_environment">this page</a>.
        </div>

        <li>
          <h4>Set up the merchant account</h4>
          <p class="mb-0">
            To use Apple Pay, you must first register in the Apple Pay developer program.
            You can register for an account <a target="_blank" href="https://developer.apple.com/account/">here</a>.
          </p>
          <p class="mb-0">
            Once you have set up your developer account with Apple Pay, you will need to create a merchant identifier.
            To learn how to create a merchant identifier, visit
            <a target="_blank" href="https://developer.apple.com/help/account/configure-app-capabilities/configure-apple-pay#create-a-merchant-identifier">this page</a>.
          </p>
          <p class="mb-0">
            If you are using Apple Pay on the web, you will need to register a merchant domain. To learn how to verify
            your domain, visit
            <a target="_blank" href="https://developer.apple.com/help/account/configure-app-capabilities/configure-apple-pay-on-the-web">this page</a>.
          </p>
        </li>
        <li>
          <h4>Configure Apple Pay settings</h4>
          <ol class="bottom-margin" type="a">
            <li>
              <p>
                Next, navigate to the Control Panel page in your gateway merchant portal, and choose the Digital Wallet option.
                Then scroll to the Apple Pay section. Enter your merchant identifier in the box as shown.
              </p>
              <p><strong>NOTE:</strong> To use the same merchant identifier with multiple gateway merchant accounts, enter your existing merchant identifier and skip to step e.</p>
              <p><img src="./assets/enter_merch_id.png" alt="A textbox to enter the Apple Pay Merchant ID."></p>
              <p>Click <strong>NEXT</strong>.</p>
            </li>
            <li>
              <p>
                On the next screen, download the Merchant ID signing request and the Payment Processing signing request files.
              </p>
              <p>
                <img src="./assets/dnload_certs.png" alt="Buttons to download the Merchant ID signing request and the Payment Processing signing request files">
              </p>
            </li>
            <li>
              <p>
                Go back to the Apple Pay portal. Navigate to the
                <a target="_blank" href="https://developer.apple.com/account/resources/identifiers/list/merchant">Certificates, Identifiers & Portals page</a>.
              </p>
              <p>
                Select your Merchant Identifier.
                Under the Apple Pay Payment Processing Certificate heading, click <strong>Create Certificate</strong>.
              </p>
              <p>
                <img src="./assets/ap_proc_cert.png" alt="Instructions and a button to create an Apple Pay Payment Processing Certificate.">
              </p>
              <p class="mb-0">In the Create a New Certificate page, click <strong>Choose File</strong>.</p>
              <p>
                <img src="./assets/choose_cert.png" alt="A button to upload a Certificate Signing Request file.">
              </p>
              <p>
                Select the Payment Processing signing request file you downloaded in the step above.
                The file will be named <strong>Payment Processing.csr</strong>.
              </p>
              <p>Click <strong>Continue</strong>.</p>
            </li>
            <li>
              <p>Repeat step <strong>c</strong>, this time choosing Apple Pay Merchant Identity Certificate.</p>
              <p>
                <img src="./assets/merch_id_cert.png" alt="A button to upload an Apple Pay Merchant Identity Certificate.">
              </p>
              <p>
                Now upload the Merchant ID signing request file. The file will be named <strong>Merchant Id.csr</strong>.
              </p>
              <p>Click <strong>Continue</strong>.</p>
            </li>
            <li>
              <p>In the next screen, click <strong>Download</strong> to download your Merchant Id certificate.</p>
              <p>
                <img src="./assets/dload_merch_cert.png" alt="The certificate details, with buttons to revoke or download the certificate.">
              </p>
              <p>The file will be named <strong>merchant_id.cer</strong>.</p>
            </li>
            <li>
              <p>Return to the gateway merchant portal.</p>
              <p>Click <strong>NEXT</strong>.</p>
              <p>
                Click <strong>Upload</strong> to upload the Merchant Id Certificate downloaded from the Apple Pay developer portal.
              </p>
              <p><img src="./assets/upload_merch_cert.png" alt="A button to upload the Merchant Id Certificate."></p>
            </li>
            <li>
              <p>You are now ready to process with Apple Pay.</p>
              <p><img src="./assets/success_msg.png" alt="A message saying 'You are registered to use Apple Pay!'"></p>
            </li>
          </ol>
        </li>
        <li>
          <h4>Using Apple Pay</h4>
          <p>
            Familiarize yourself with Apple Pay guidelines for branding. Use the following links as a starting point.
          </p>
          <ul>
            <li>
              <a target="_blank" href="https://developer.apple.com/apple-pay/marketing/">Apple Pay Marketing Guidelines</a>
            </li>
            <li>
              <a target="_blank" href="https://developer.apple.com/design/human-interface-guidelines/apple-pay">Apple Pay UI Guidelines</a>
            </li>
          </ul>
          <p>
            Ensure your site is hosted on a verified domain.
            Follow the instructions on
            <a target="_blank" href="https://developer.apple.com/documentation/apple_pay_on_the_web/displaying_apple_pay_buttons_using_javascript">this page</a>
            to add an Apple Pay button to your site.
          </p>
          <p>
            Build your payment flow using the Apple Pay APIs. Begin by creating an Apple Pay session. See below for an
            example session object.
          </p>
          <pre>
const request = {
  countryCode: 'US',
  currencyCode: 'USD',
  supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
  merchantCapabilities: ['supports3DS'],
  total: { label: 'Your Merchant Name', amount: '10.00' },
}
const session = new ApplePaySession(3, request);
          </pre>
          <p>
            Add an event handler to the session object for the <code>onvalidatemerchant</code> event. Use our gateway
            API to create a session, passing the <code>validationURL</code> parameter received from the event. The
            documentation for our Apple Session endpoint can be found
            <a target="_blank" href="/api/v2#tag/apple-session/paths/~1apple-pay~1session/post">here</a>.
          </p>
          <p><strong>Note</strong>: this endpoint requires a public source key.</p>
          <p>
            If the merchant account was boarded properly, you should receive a valid Apple Session in the response.
            Pass the response body directly to the session's <code>completeMerchantValidation</code> method.
          </p>
          <p><strong>Note</strong>: the session is only valid for 5 minutes.</p>
          <p>See below for a full example.</p>
          <pre>
session.onvalidatemerchant = async function (event) {
  try {
    const baseURL = 'APPLE_SESSION_API_ENDPOINT'; // Use the endpoint obtained from the gateway docs
    const response = await fetch(baseURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        url: event.validationURL,
      });
    });
    const appleSession = await response.json();
    session.completeMerchantValidation(appleSession);
  } catch (e) {
    session.abort();
    console.error('Could not get an Apple Pay session');
  }
}
          </pre>
        </li>
        <li>
          <h4>Completing Checkout</h4>
          <p>
            At this point, the payment flow will display the Apple Pay payment options to the customer. The customer
            will complete the checkout with Apple Pay. When the customer clicks the Pay button, it will trigger the
            <code>onpaymentauthorized</code> event. You must handle this event, as shown in the example below.
          </p>
          <pre>
session.onpaymentauthorized = async function (event) {
  // Add handling logic here to send the encrypted payload to your back-end server
}
          </pre>
          <p>The event object will contain an encrypted Apple Pay payload.</p>
          <pre>
// event object structure
{
  "payment": {
    "token": {
      "paymentData": "ENCRYPTED_PAYLOAD"
    }
  }
}
          </pre>
        </li>
        <li>
          <h4> Submitting the charge request</h4>
          <p>
            Validate the charge request and amount on your backend. Then, create a
            <a target="_blank" href="/api/v2#tag/processing-charges/paths/~1transactions~1charge/post">
              Digital Wallet Charge
            </a>
            request object containing the encrypted payload obtained from Apple Pay.
            The <code>source</code> field should be set to <code>"applepay"</code>, and the
            encrypted data should be passed in the <code>token</code> field.
          </p>
          <p>
            <strong>Note</strong>: The encrypted data only contains the information needed to process a charge using
            the customer's saved wallet. You will still need to supply the charge amount and any billing information,
            if applicable.
          </p>
          <p>Sample API request body:</p>
          <pre>
{
  "amount": 1.5,
  "source": "applepay",
  "token": "ENCRYPTED_PAYLOAD",
  "avs_zip": "12345",
  "avs_address": "123 Main St."
}
          </pre>
          <p>
            Finally, submit request to the gateway using the
            <a href="/api/v2#tag/processing-charges/paths/~1transactions~1charge/post">/charge endpoint</a>.
          </p>
          <p>Sample curl request:</p>
          <pre>
curl --request POST \
  --url API_DOMAIN/api/v2/transactions/charge \
  --user 'API_KEY:API_KEY_PIN' \
  --header 'Content-Type: application/json' \
  --data '{
    "amount": 1.5,
    "source": "applepay",
    "token": "ENCRYPTED_PAYLOAD",
    "avs_zip": "12345",
    "avs_address": "123 Main St."
  }'
          </pre>
        </li>
      </ol>
    </section>
  </div>
</template>

<style lang="scss" scoped>
@import '../../styles/bulma-wrapper.scss';

pre {
  margin: 20px 3rem 20px 0;
}

ol.bottom-margin > li {
  margin-bottom: 20px;
}

.body-wrapper {
  width: 80%;
  margin: auto;
}

section {
  margin-bottom: 25px;
}

a > code {
  text-decoration: underline;
}
</style>
